import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  fetchAccounts, fetchAccountFunds, createAccount, updateAccount, deleteAccount,
  testAccountConnection, setDataFeedAccount, reconnectAccount,
  getAngelOneLoginUrl, getAngelOneDefaultKey, getKiteLoginUrl,
  fetchStrategyConfigs, createStrategyConfig, updateStrategyConfig, deleteStrategyConfig,
} from '../api';

// Default configs keyed by "entry_type|index|trading_day"
const STRATEGY_DEFAULTS = {
  'atm_straddle|Nifty|Monday':     { adjustment_points: 100, strike_interval: 100, start_time: '09:45' },
  'atm_straddle|Nifty|Tuesday':    { adjustment_points: 50,  strike_interval: 50,  start_time: '09:45' },
  'atm_straddle|BankNifty|Wednesday': { adjustment_points: 100, strike_interval: 100, start_time: '09:45' },
  'atm_straddle|Sensex|Wednesday': { adjustment_points: 300, strike_interval: 100, start_time: '09:45' },
  'atm_straddle|Sensex|Thursday':  { adjustment_points: 100, strike_interval: 100, start_time: '09:45' },
  'opening_range|Nifty|Monday':        { adjustment_points: 100, strike_interval: 100, start_time: '09:30' },
  'opening_range|Nifty|Tuesday':       { adjustment_points: 50,  strike_interval: 50,  start_time: '09:30' },
  'opening_range|BankNifty|Wednesday': { adjustment_points: 100, strike_interval: 100, start_time: '09:30' },
  'opening_range|Sensex|Wednesday':    { adjustment_points: 300, strike_interval: 100, start_time: '09:30' },
  'opening_range|Sensex|Thursday':     { adjustment_points: 100, strike_interval: 100, start_time: '09:30' },
};

function getDefaults(entryType, index, tradingDay) {
  const isHundredStepIndex = index === 'Sensex' || index === 'BankNifty';
  if (entryType === 'manual_single_leg') {
    return { adjustment_points: 0, strike_interval: isHundredStepIndex ? 100 : 50, start_time: '09:20', exit_time: '15:15' };
  }
  if (entryType === 'rolling_strangle') {
    return { adjustment_points: 300, strike_interval: isHundredStepIndex ? 100 : 50, start_time: '09:20', exit_time: '15:15', offset_points: 500, rolling_points: 300 };
  }
  if (entryType === 'otm_strangle') {
    return { adjustment_points: isHundredStepIndex ? 100 : 50, strike_interval: isHundredStepIndex ? 100 : 50, start_time: '09:45', exit_time: '14:40', otm_strikes: 1 };
  }
  if (entryType === 'ladder_straddle') {
    return { adjustment_points: 100, strike_interval: isHundredStepIndex ? 100 : 50, start_time: '09:20', exit_time: '15:15', offset_points: 100 };
  }
  if (entryType === 'indicator_aligned') {
    return { adjustment_points: 0, strike_interval: isHundredStepIndex ? 100 : 50, start_time: '09:30', exit_time: '15:15' };
  }
  if (entryType === 'major_stocks_aligned') {
    return { adjustment_points: 0, strike_interval: isHundredStepIndex ? 100 : 50, start_time: '09:30', exit_time: '15:15' };
  }
  if (entryType === 'hedge_only') {
    return { adjustment_points: 0, strike_interval: isHundredStepIndex ? 100 : 50, start_time: '09:20', exit_time: '15:15' };
  }
  const key = `${entryType}|${index}|${tradingDay}`;
  if (STRATEGY_DEFAULTS[key]) return STRATEGY_DEFAULTS[key];
  // Fallback: Sensex always 100 interval, Nifty expiry 50, else 100
  const si = isHundredStepIndex ? 100 : (tradingDay === 'Tuesday' ? 50 : 100);
  return {
    adjustment_points: si,
    strike_interval: si,
    start_time: entryType === 'opening_range' ? '09:30' : '09:45',
  };
}

function formatSL(inst) {
  const t = inst.sl_type || 'none';
  if (t === 'spot') return `Spot: ${inst.sl_lower || '—'} / ${inst.sl_upper || '—'}`;
  if (t === 'premium') return `Premium ≥ ${inst.sl_premium}`;
  if (t === 'amount') return `Loss ≥ ₹${Number(inst.sl_amount || 0).toLocaleString('en-IN')}`;
  if ((inst.sl_lower > 0 || inst.sl_upper > 0)) return `Spot: ${inst.sl_lower || '—'} / ${inst.sl_upper || '—'}`;
  return 'Off';
}

function StrategyInstance({ instance, accounts, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  // Collapsed by default on mobile — only Type + Status visible.
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    entry_type: instance.entry_type || 'atm_straddle',
    index: instance.index || 'Nifty',
    trading_day: instance.trading_day || 'Tuesday',
    adjustment_points: instance.adjustment_points || 50,
    strike_interval: instance.strike_interval || 50,
    start_time: instance.start_time || '09:20',
    exit_time: instance.exit_time || '14:40',
    strike_mode: instance.strike_mode || 'ATM',
    itm_points: instance.itm_points || 0,
    lots: instance.lots || 1,
    account_id: instance.account_id || '',
    is_active: instance.is_active !== false,
    sl_type: instance.sl_type || 'none',
    sl_lower: instance.sl_lower || 0,
    sl_upper: instance.sl_upper || 0,
    sl_premium: instance.sl_premium || 0,
    sl_amount: instance.sl_amount || 0,
    offset_points: instance.offset_points || 500,
    rolling_points: instance.rolling_points || 300,
    hedge_enabled: instance.hedge_enabled || false,
    hedge_premium: instance.hedge_premium || (instance.index === 'Sensex' ? 10 : 3),
    hedge_lots: instance.hedge_lots || 0,
    hedge_strike_mode: instance.hedge_strike_mode || 'premium',
    hedge_offset_strikes: instance.hedge_offset_strikes || 5,
    place_immediately: instance.place_immediately || false,
    bullish_reference: instance.bullish_reference || 'prev_day_close',
    bearish_reference: instance.bearish_reference || 'prev_day_close',
    stock_weight_threshold: instance.stock_weight_threshold || 3,
    otm_strikes: instance.otm_strikes || 1,
    target_amount: instance.target_amount || 0,
    target_exit_pct: instance.target_exit_pct || 0,
    sl_partial_amount: instance.sl_partial_amount || 0,
    sl_partial_exit_pct: instance.sl_partial_exit_pct || 0,
    timed_lot_add_enabled: instance.timed_lot_add_enabled || false,
    timed_lot_add_interval_min: instance.timed_lot_add_interval_min || 30,
    timed_lot_add_lots: instance.timed_lot_add_lots || 1,
    timed_lot_add_max_lots: instance.timed_lot_add_max_lots || (instance.lots || 1),
    timed_lot_add_pnl_gate_enabled: instance.timed_lot_add_pnl_gate_enabled || false,
    timed_lot_add_pnl_threshold_pct: instance.timed_lot_add_pnl_threshold_pct || 0,
  });

  const handleSave = () => { onSave(instance.id, form); setEditing(false); };

  // Always render summary; when `editing` is true, append the edit panel
  // as a sibling row. On mobile the two rows are styled to merge into
  // one continuous card (see .row-edit-attached in App.css). On desktop
  // the edit panel slides under the summary row as a full-width row.
  return (
    <>
      <tr className={`inst-tr ${(expanded || editing) ? 'row-expanded-card' : 'row-collapsed'} ${editing ? 'row-editing' : ''}`}>
        <td
          data-label="Type"
          className="cell-symbol cell-show-collapsed"
          style={{ cursor: 'pointer' }}
          onClick={() => setExpanded(v => !v)}
        >
          <span className="card-chevron" aria-hidden="true">{(expanded || editing) ? '▾' : '▸'}</span>
          <span className={`instance-badge ${instance.entry_type === 'opening_range' ? 'badge-range' : instance.entry_type === 'manual_single_leg' ? 'badge-manual' : instance.entry_type === 'rolling_strangle' ? 'badge-strangle' : instance.entry_type === 'otm_strangle' ? 'badge-strangle' : instance.entry_type === 'ladder_straddle' ? 'badge-ladder' : instance.entry_type === 'indicator_aligned' ? 'badge-indicator' : instance.entry_type === 'major_stocks_aligned' ? 'badge-stocks' : instance.entry_type === 'hedge_only' ? 'badge-hedge' : instance.entry_type === 'dynamic_atm_straddle' ? 'badge-dynamic' : 'badge-atm'}`}>{instance.entry_type === 'opening_range' ? 'Opening Range' : instance.entry_type === 'manual_single_leg' ? 'Manual Trade' : instance.entry_type === 'rolling_strangle' ? 'Rolling Strangle' : instance.entry_type === 'otm_strangle' ? 'OTM Strangle' : instance.entry_type === 'ladder_straddle' ? 'Ladder Straddle' : instance.entry_type === 'indicator_aligned' ? 'Indicator Aligned' : instance.entry_type === 'major_stocks_aligned' ? 'Major Stocks Aligned' : instance.entry_type === 'hedge_only' ? 'Hedge Only' : instance.entry_type === 'dynamic_atm_straddle' ? 'Dynamic ATM' : 'ATM Straddle'}</span>
        </td>
        <td data-label="Index" className="cell-show-collapsed">{instance.index}</td>
        <td data-label="Day">{instance.trading_day}</td>
        <td data-label="Adj Pts">{instance.adjustment_points}</td>
        <td data-label="Strike">{instance.strike_interval}</td>
        <td data-label="Stop Loss"><span className="sl-summary">{formatSL(instance)}</span></td>
        <td data-label="Hedge"><span className={`hedge-summary ${instance.hedge_enabled ? 'hedge-on' : ''}`}>{instance.hedge_enabled ? `₹${instance.hedge_premium || (instance.index === 'Sensex' ? 10 : 3)}` : 'Off'}</span></td>
        <td data-label="Account" className="cell-show-collapsed"><span className="instance-account">{instance.account_name || 'Paper'}</span></td>
        <td data-label="Entry" className="cell-show-collapsed">{instance.start_time || '09:20'}</td>
        <td data-label="Exit" className="cell-show-collapsed">{instance.exit_time || '14:40'}</td>
        <td data-label="Mode">{instance.strike_mode || 'ATM'}{instance.strike_mode === 'ITM' ? ` ${instance.itm_points}` : ''}</td>
        <td data-label="Lots">{instance.lots || 1}</td>
        <td data-label="Status" className="cell-show-collapsed"><span className={`instance-status ${instance.is_active !== false ? 'active' : 'inactive'}`}>{instance.is_active !== false ? '● Active' : '○ Off'}</span></td>
        <td data-label="Actions" className="inst-actions-cell cell-actions">
          {editing ? (
            <button className="btn-sm" onClick={() => setEditing(false)}>Cancel</button>
          ) : (
            <button className="btn-sm" onClick={() => { setEditing(true); setExpanded(true); }}>Edit</button>
          )}
          <button className="btn-sm btn-danger" onClick={() => onDelete(instance.id)}>✕</button>
        </td>
      </tr>
      {editing && (
      <tr className="inst-tr-edit row-expanded row-edit-attached"><td colSpan={14} className="cell-full">
    <div className="strat-edit-card">
      <div className="strat-edit-section">
        <span className="strat-edit-label">Strategy</span>
        <div className="strat-edit-row">
          <div className="form-field">
            <label>Type</label>
            <select value={form.entry_type} onChange={e => setForm({...form, entry_type: e.target.value})}>
              <option value="atm_straddle">ATM Straddle</option>
              <option value="dynamic_atm_straddle">🧠 Dynamic ATM Straddle</option>
              <option value="opening_range">Opening Range</option>
              <option value="rolling_strangle">Rolling Strangle</option>
              <option value="otm_strangle">OTM Strangle</option>
              <option value="ladder_straddle">Ladder Straddle</option>
              <option value="indicator_aligned">Indicator Aligned</option>
              <option value="major_stocks_aligned">Major Stocks Aligned</option>
              <option value="hedge_only">Hedge Only (Long Strangle)</option>
            </select>
            <small className="field-hint">{form.entry_type === 'hedge_only' ? 'Hedge Only: BUYS one OTM CE + one OTM PE (long strangle). No selling. Pure long-vol/disaster hedge.' : form.entry_type === 'otm_strangle' ? 'OTM Strangle: sells CE at ATM + N×interval and PE at ATM − N×interval. Rolls on Adj Pts move just like ATM Straddle.' : 'ATM Straddle sells at current price. Opening Range uses 15-min candle. Rolling Strangle = auto OTM strangle with rolls. Ladder Straddle = 3 straddles with offset rolls. Indicator Aligned = sells PE/CE when all 4 dashboard indicators agree. Major Stocks Aligned = sells when all constituents ≥ weight% move one way. For one-off single/multi-leg orders use the Quick Order page instead.'}</small>
          </div>
          <div className="form-field">
            <label>Index</label>
            <select value={form.index} onChange={e => setForm({...form, index: e.target.value})}>
              <option value="Nifty">Nifty</option>
              <option value="BankNifty">BankNifty</option>
              <option value="Sensex">Sensex</option>
            </select>
            <small className="field-hint">Which index to trade options on</small>
          </div>
          {form.entry_type !== 'rolling_strangle' && form.entry_type !== 'ladder_straddle' && form.entry_type !== 'indicator_aligned' && form.entry_type !== 'major_stocks_aligned' && form.entry_type !== 'hedge_only' && (<>
          <div className="form-field">
            <label>Day</label>
            <select value={form.trading_day} onChange={e => setForm({...form, trading_day: e.target.value})}>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
              <option value="Daily">Daily</option>
            </select>
            <small className="field-hint">Which day of the week to run this strategy</small>
          </div>
          {form.entry_type !== 'dynamic_atm_straddle' && (
          <div className="form-field">
            <label>Adj Pts</label>
            <input type="number" value={form.adjustment_points} onChange={e => setForm({...form, adjustment_points: parseInt(e.target.value) || 50})} min="10" step="10" />
            <small className="field-hint">Re-enter at new ATM if index moves these many points</small>
          </div>
          )}
          </>)}
          {form.entry_type === 'indicator_aligned' && (<>
          <div className="form-field">
            <label>Day</label>
            <select value={form.trading_day} onChange={e => setForm({...form, trading_day: e.target.value})}>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
              <option value="Daily">Daily</option>
            </select>
            <small className="field-hint">Day to run this strategy</small>
          </div>
          <div className="form-field">
            <label>Bullish Ref</label>
            <select value={form.bullish_reference} onChange={e => setForm({...form, bullish_reference: e.target.value})}>
              <option value="prev_day_close">Prev Day Close</option>
              <option value="prev_day_low">Prev Day Low</option>
            </select>
            <small className="field-hint">PE strike reference when all 4 bullish (floors to interval)</small>
          </div>
          <div className="form-field">
            <label>Bearish Ref</label>
            <select value={form.bearish_reference} onChange={e => setForm({...form, bearish_reference: e.target.value})}>
              <option value="prev_day_close">Prev Day Close</option>
              <option value="prev_day_high">Prev Day High</option>
            </select>
            <small className="field-hint">CE strike reference when all 4 bearish (ceils to interval)</small>
          </div>
          </>)}
          {form.entry_type === 'major_stocks_aligned' && (<>
          <div className="form-field">
            <label>Day</label>
            <select value={form.trading_day} onChange={e => setForm({...form, trading_day: e.target.value})}>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
              <option value="Daily">Daily</option>
            </select>
            <small className="field-hint">Day to run this strategy</small>
          </div>
          <div className="form-field">
            <label>Weight ≥ (%)</label>
            <select value={form.stock_weight_threshold} onChange={e => setForm({...form, stock_weight_threshold: parseFloat(e.target.value)})}>
              <option value={2}>2%</option>
              <option value={3}>3%</option>
              <option value={4}>4%</option>
              <option value={5}>5%</option>
            </select>
            <small className="field-hint">Only stocks with weight ≥ this are considered "major"</small>
          </div>
          <div className="form-field">
            <label>Bullish Ref</label>
            <select value={form.bullish_reference} onChange={e => setForm({...form, bullish_reference: e.target.value})}>
              <option value="prev_day_close">Prev Day Close</option>
              <option value="prev_day_low">Prev Day Low</option>
            </select>
            <small className="field-hint">PE strike reference when all stocks bullish</small>
          </div>
          <div className="form-field">
            <label>Bearish Ref</label>
            <select value={form.bearish_reference} onChange={e => setForm({...form, bearish_reference: e.target.value})}>
              <option value="prev_day_close">Prev Day Close</option>
              <option value="prev_day_high">Prev Day High</option>
            </select>
            <small className="field-hint">CE strike reference when all stocks bearish</small>
          </div>
          </>)}
          {form.entry_type === 'rolling_strangle' && (<>
          <div className="form-field">
            <label>Offset Pts</label>
            <input type="number" value={form.offset_points} onChange={e => setForm({...form, offset_points: parseInt(e.target.value) || 500})} min="50" step="50" />
            <small className="field-hint">Initial OTM offset from ATM (±points)</small>
          </div>
          <div className="form-field">
            <label>Rolling Pts</label>
            <input type="number" value={form.rolling_points} onChange={e => setForm({...form, rolling_points: parseInt(e.target.value) || 300})} min="50" step="50" />
            <small className="field-hint">Points movement to trigger adjustment</small>
          </div>
          </>)}
          {form.entry_type === 'ladder_straddle' && (<>
          <div className="form-field">
            <label>Offset Pts</label>
            <input type="number" value={form.offset_points} onChange={e => setForm({...form, offset_points: parseInt(e.target.value) || 100})} min="50" step="50" />
            <small className="field-hint">Distance between ladder straddle strikes</small>
          </div>
          </>)}
          {form.entry_type === 'otm_strangle' && (
          <div className="form-field">
            <label>OTM Strikes</label>
            <input type="number" value={form.otm_strikes} onChange={e => setForm({...form, otm_strikes: parseInt(e.target.value) || 1})} min="1" step="1" />
            <small className="field-hint">1 = +1 step OTM, 2 = +2 steps, … (CE = ATM + N×interval, PE = ATM − N×interval)</small>
          </div>
          )}
          {form.entry_type === 'hedge_only' && (
          <div className="form-field">
            <label>Day</label>
            <select value={form.trading_day} onChange={e => setForm({...form, trading_day: e.target.value})}>
              <option value="Daily">Daily</option>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
            </select>
            <small className="field-hint">Run hedge on these days only</small>
          </div>
          )}
          <div className="form-field">
            <label>Strike Int</label>
            <select value={form.strike_interval} onChange={e => setForm({...form, strike_interval: parseInt(e.target.value)})}>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <small className="field-hint">Strike price rounding interval (50 or 100)</small>
          </div>
        </div>
      </div>
      <div className="strat-edit-section">
        <span className="strat-edit-label">Stop Loss</span>
        <div className="strat-edit-row">
          <div className="form-field">
            <label>SL Type</label>
            <select value={form.sl_type} onChange={e => setForm({...form, sl_type: e.target.value})}>
              <option value="none">None</option>
              {form.entry_type !== 'hedge_only' && <option value="spot">Spot Level</option>}
              <option value="premium">{form.entry_type === 'hedge_only' ? 'Combined Premium ≤' : 'Combined Premium ≥'}</option>
              <option value="amount">Loss Amount (₹)</option>
            </select>
            <small className="field-hint">Type of stop loss trigger</small>
          </div>
          {form.sl_type === 'spot' && (<>
            <div className="form-field">
              <label>SL Lower</label>
              <input type="number" value={form.sl_lower} onChange={e => setForm({...form, sl_lower: parseFloat(e.target.value) || 0})} min="0" step="50" placeholder="0 = off" />
              <small className="field-hint">Exit if spot drops below this level</small>
            </div>
            <div className="form-field">
              <label>SL Upper</label>
              <input type="number" value={form.sl_upper} onChange={e => setForm({...form, sl_upper: parseFloat(e.target.value) || 0})} min="0" step="50" placeholder="0 = off" />
              <small className="field-hint">Exit if spot rises above this level</small>
            </div>
          </>)}
          {form.sl_type === 'premium' && (
            <div className="form-field">
              <label>Premium Threshold</label>
              <input type="number" value={form.sl_premium} onChange={e => setForm({...form, sl_premium: parseFloat(e.target.value) || 0})} min="0" step="10" placeholder="e.g. 350" />
              <small className="field-hint">{form.entry_type === 'hedge_only' ? 'Exit if combined CE+PE premium drops below this (long decay)' : 'Exit if combined CE+PE premium exceeds this'}</small>
            </div>
          )}
          {form.sl_type === 'amount' && (
            <div className="form-field">
              <label>Max Loss (₹)</label>
              <input type="number" value={form.sl_amount} onChange={e => setForm({...form, sl_amount: parseFloat(e.target.value) || 0})} min="0" step="500" placeholder="e.g. 5000" />
              <small className="field-hint">Exit if strategy loss exceeds this amount</small>
            </div>
          )}
        </div>
      </div>
      {(form.entry_type === 'atm_straddle' || form.entry_type === 'otm_strangle') && (
      <div className="strat-edit-section">
        <span className="strat-edit-label">Partial Exits (one-shot per day)</span>
        <div className="strat-edit-row">
          <div className="form-field">
            <label>Target Amount (₹)</label>
            <input type="number" value={form.target_amount} onChange={e => setForm({...form, target_amount: parseFloat(e.target.value) || 0})} min="0" step="500" placeholder="0 = off" />
            <small className="field-hint">When profit ≥ this, exit a slice of lots</small>
          </div>
          <div className="form-field">
            <label>Target Exit %</label>
            <input type="number" value={form.target_exit_pct} onChange={e => setForm({...form, target_exit_pct: parseFloat(e.target.value) || 0})} min="0" max="99" step="5" placeholder="e.g. 30" />
            <small className="field-hint">% of original lots to exit on target hit</small>
          </div>
          <div className="form-field">
            <label>Partial SL Amount (₹)</label>
            <input type="number" value={form.sl_partial_amount} onChange={e => setForm({...form, sl_partial_amount: parseFloat(e.target.value) || 0})} min="0" step="500" placeholder="0 = off" />
            <small className="field-hint">When loss ≥ this, exit a slice of lots</small>
          </div>
          <div className="form-field">
            <label>Partial SL Exit %</label>
            <input type="number" value={form.sl_partial_exit_pct} onChange={e => setForm({...form, sl_partial_exit_pct: parseFloat(e.target.value) || 0})} min="0" max="99" step="5" placeholder="e.g. 30" />
            <small className="field-hint">% of original lots to exit on partial SL hit</small>
          </div>
        </div>
      </div>
      )}
      {form.entry_type === 'atm_straddle' && (
      <div className="strat-edit-section">
        <span className="strat-edit-label">Timed Lot Additions</span>
        <div className="strat-edit-row">
          <div className="form-field">
            <label>Enable Timed Add</label>
            <select value={form.timed_lot_add_enabled ? 'on' : 'off'} onChange={e => setForm({...form, timed_lot_add_enabled: e.target.value === 'on'})}>
              <option value="off">Off</option>
              <option value="on">On</option>
            </select>
            <small className="field-hint">When ON, add sell lots on a fixed interval until max lots</small>
          </div>
          <div className="form-field">
            <label>Interval (min)</label>
            <input type="number" value={form.timed_lot_add_interval_min} onChange={e => setForm({...form, timed_lot_add_interval_min: Math.max(1, parseInt(e.target.value) || 30)})} min="1" step="1" disabled={!form.timed_lot_add_enabled} />
            <small className="field-hint">Example: 30 means add checks every 30 minutes</small>
          </div>
          <div className="form-field">
            <label>Lots Per Add</label>
            <input type="number" value={form.timed_lot_add_lots} onChange={e => setForm({...form, timed_lot_add_lots: Math.max(1, parseInt(e.target.value) || 1)})} min="1" step="1" disabled={!form.timed_lot_add_enabled} />
            <small className="field-hint">Lots to add each timed cycle</small>
          </div>
          <div className="form-field">
            <label>Max Total Lots</label>
            <input type="number" value={form.timed_lot_add_max_lots} onChange={e => setForm({...form, timed_lot_add_max_lots: Math.max(form.lots || 1, parseInt(e.target.value) || (form.lots || 1))})} min={form.lots || 1} step="1" disabled={!form.timed_lot_add_enabled} />
            <small className="field-hint">No further timed adds once this cap is reached</small>
          </div>
          <div className="form-field">
            <label>P&L% Gate</label>
            <select value={form.timed_lot_add_pnl_gate_enabled ? 'on' : 'off'} onChange={e => setForm({...form, timed_lot_add_pnl_gate_enabled: e.target.value === 'on'})} disabled={!form.timed_lot_add_enabled}>
              <option value="off">Off</option>
              <option value="on">On</option>
            </select>
            <small className="field-hint">If ON and threshold is reached, timed add is skipped</small>
          </div>
          <div className="form-field">
            <label>P&L Threshold %</label>
            <input type="number" value={form.timed_lot_add_pnl_threshold_pct} onChange={e => setForm({...form, timed_lot_add_pnl_threshold_pct: parseFloat(e.target.value) || 0})} min="0" step="0.1" disabled={!form.timed_lot_add_enabled || !form.timed_lot_add_pnl_gate_enabled} placeholder="e.g. 15" />
            <small className="field-hint">Compared against open P&L% of combined sold premium</small>
          </div>
        </div>
      </div>
      )}
      {form.entry_type === 'hedge_only' && (
      <div className="strat-edit-section">
        <span className="strat-edit-label">Hedge Selection</span>
        <div className="strat-edit-row">
          <div className="form-field">
            <label>Strike Mode</label>
            <select value={form.hedge_strike_mode} onChange={e => setForm({...form, hedge_strike_mode: e.target.value})}>
              <option value="premium">Premium-based</option>
              <option value="offset">Offset-based</option>
            </select>
            <small className="field-hint">Premium: scan OTM for ≈ target ₹. Offset: ATM ± N strikes</small>
          </div>
          {form.hedge_strike_mode === 'premium' && (
            <div className="form-field">
              <label>Target Premium <span className="unit">(₹)</span></label>
              <input type="number" value={form.hedge_premium} onChange={e => setForm({...form, hedge_premium: parseFloat(e.target.value) || 3})} min="0.5" step="0.5" />
              <small className="field-hint">Nifty ≈ ₹3 · Sensex ≈ ₹10</small>
            </div>
          )}
          {form.hedge_strike_mode === 'offset' && (
            <div className="form-field">
              <label>Strikes OTM</label>
              <input type="number" value={form.hedge_offset_strikes} onChange={e => setForm({...form, hedge_offset_strikes: parseInt(e.target.value) || 5})} min="1" step="1" />
              <small className="field-hint">CE = ATM + N×interval, PE = ATM − N×interval</small>
            </div>
          )}
        </div>
      </div>
      )}
      {form.entry_type !== 'manual_single_leg' && form.entry_type !== 'hedge_only' && (
      <div className="strat-edit-section">
        <span className="strat-edit-label">Hedge</span>
        <div style={{flex: 1}}>
          <div className="hedge-toggle-row">
            <label className="hedge-switch">
              <input type="checkbox" checked={form.hedge_enabled} onChange={e => setForm({...form, hedge_enabled: e.target.checked})} />
              <span className="slider"></span>
            </label>
            <span className="hedge-toggle-label" onClick={() => setForm({...form, hedge_enabled: !form.hedge_enabled})}>
              OTM Protection
              <span className={`hedge-badge ${form.hedge_enabled ? 'badge-on' : 'badge-off'}`}>{form.hedge_enabled ? 'ON' : 'OFF'}</span>
            </span>
          </div>
          {form.hedge_enabled && (
            <div className="hedge-fields">
              <div className="form-field">
                <label>Target Premium <span className="unit">(₹)</span></label>
                <input type="number" value={form.hedge_premium} onChange={e => setForm({...form, hedge_premium: parseFloat(e.target.value) || 3})} min="0.5" step="0.5" />
                <small className="field-hint">Nifty ≈ ₹3 · Sensex ≈ ₹10</small>
              </div>
              <div className="form-field">
                <label>Hedge Lots</label>
                <input type="number" value={form.hedge_lots} onChange={e => setForm({...form, hedge_lots: parseInt(e.target.value) || 0})} min="0" step="1" />
                <small className="field-hint">0 = match strategy lots</small>
              </div>
            </div>
          )}
        </div>
      </div>
      )}
      <div className="strat-edit-section">
        <span className="strat-edit-label">Execution</span>
        <div className="strat-edit-row">
          <div className="form-field">
            <label>Account</label>
            <select value={form.account_id} onChange={e => setForm({...form, account_id: e.target.value ? parseInt(e.target.value) : ''})}>
              <option value="">Paper Trading</option>
              {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
            </select>
            <small className="field-hint">Broker account to execute on. Paper = simulated</small>
          </div>
          <div className="form-field form-field-wide">
            <label>Entry</label>
            <input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} />
            <small className="field-hint">Time to enter the position</small>
          </div>
          <div className="form-field form-field-wide">
            <label>Exit</label>
            <input type="time" value={form.exit_time} onChange={e => setForm({...form, exit_time: e.target.value})} />
            <small className="field-hint">Time to square off all positions</small>
          </div>
          {form.entry_type !== 'hedge_only' && (<>
          <div className="form-field">
            <label>Mode</label>
            <select value={form.strike_mode} onChange={e => setForm({...form, strike_mode: e.target.value})}>
              <option value="ATM">ATM</option>
              <option value="ITM">ITM</option>
            </select>
            <small className="field-hint">ATM = at the money. ITM = sell deeper in the money</small>
          </div>
          {form.strike_mode === 'ITM' && (
            <div className="form-field">
              <label>ITM Pts</label>
              <input type="number" value={form.itm_points} onChange={e => setForm({...form, itm_points: parseInt(e.target.value) || 0})} min="0" step="50" />
              <small className="field-hint">How many points ITM to sell (CE strike below, PE strike above)</small>
            </div>
          )}
          </>)}
          <div className="form-field">
            <label>Lots</label>
            <input type="number" value={form.lots} onChange={e => setForm({...form, lots: parseInt(e.target.value) || 1})} min="1" />
            <small className="field-hint">Number of lots per leg</small>
          </div>
          <div className="form-field form-field-check">
            <label><input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} /> Active</label>
          </div>
        </div>
      </div>
      <div className="strat-edit-actions">
        <button className="btn-sm btn-primary" onClick={handleSave}>Save</button>
        <button className="btn-sm btn-warn" onClick={() => { const updated = {...form, place_immediately: true}; setForm(updated); onSave(instance.id, updated); setEditing(false); }}>⚡ Save &amp; Place Now</button>
        <button className="btn-sm" onClick={() => setEditing(false)}>Cancel</button>
      </div>
    </div>
    </td></tr>
      )}
    </>
  );
}

export default function Settings() {
  const location = useLocation();
  const [accounts, setAccounts] = useState([]);
  const [accountFunds, setAccountFunds] = useState({});
  const [strategyConfigs, setStrategyConfigs] = useState([]);
  const [strategyTypes, setStrategyTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingAccount, setEditingAccount] = useState(null);
  const [showAddAccount, setShowAddAccount] = useState(false);
  // Collapsed/expanded state for Account cards on mobile, keyed by account id.
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const toggleAccountExpanded = (id) =>
    setExpandedAccounts(prev => ({ ...prev, [id]: !prev[id] }));
  const [newAccount, setNewAccount] = useState({ broker: 'angelone', api_key: '', api_secret: '', client_id: '', password: '', mpin: '', totp_secret: '', paper_trading: true });
  const [useDefaultKey, setUseDefaultKey] = useState(true);
  const [defaultApiKey, setDefaultApiKey] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);
  const [savingAccount, setSavingAccount] = useState(false);
  const [addingStrategy, setAddingStrategy] = useState(false);
  const [newInstance, setNewInstance] = useState({ entry_type: 'atm_straddle', index: 'Nifty', trading_day: 'Tuesday', adjustment_points: 50, strike_interval: 50, start_time: '09:45', exit_time: '14:40', strike_mode: 'ATM', itm_points: 0, lots: 1, account_id: '', is_active: true, sl_type: 'none', sl_lower: 0, sl_upper: 0, sl_premium: 0, sl_amount: 0, hedge_enabled: false, hedge_premium: 3, hedge_lots: 0, hedge_strike_mode: 'premium', hedge_offset_strikes: 5, place_immediately: false, bullish_reference: 'prev_day_close', bearish_reference: 'prev_day_close', stock_weight_threshold: 3, otm_strikes: 1, target_amount: 0, target_exit_pct: 0, sl_partial_amount: 0, sl_partial_exit_pct: 0, timed_lot_add_enabled: false, timed_lot_add_interval_min: 30, timed_lot_add_lots: 1, timed_lot_add_max_lots: 1, timed_lot_add_pnl_gate_enabled: false, timed_lot_add_pnl_threshold_pct: 0 });
  const [filterType, setFilterType] = useState('');
  const [filterIndex, setFilterIndex] = useState('');
  const [filterDay, setFilterDay] = useState('');

  const updateNewWithDefaults = (field, value) => {
    const next = { ...newInstance, [field]: value };
    const defs = getDefaults(next.entry_type, next.index, next.trading_day);
    if (field === 'index') next.hedge_premium = value === 'Sensex' ? 10 : 3;
    setNewInstance({ ...next, ...defs, exit_time: next.exit_time, strike_mode: next.strike_mode, itm_points: next.itm_points, lots: next.lots, account_id: next.account_id, is_active: next.is_active });
  };

  // Handle OAuth callback navigation state
  useEffect(() => {
    if (location.state?.authMessage) {
      if (location.state.authOk) {
        setConnectionResult({ status: 'ok', message: location.state.authMessage });
      } else {
        setError(location.state.authMessage);
      }
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location]);

  const loadSettings = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [accRes, cfgRes, keyRes] = await Promise.allSettled([fetchAccounts(), fetchStrategyConfigs(), getAngelOneDefaultKey()]);
      if (accRes.status === 'fulfilled') setAccounts(accRes.value.data?.accounts || []);
      if (cfgRes.status === 'fulfilled') {
        setStrategyConfigs(cfgRes.value.data?.configs || []);
        setStrategyTypes(cfgRes.value.data?.strategy_types || []);
      }
      if (keyRes.status === 'fulfilled' && keyRes.value.data?.api_key) setDefaultApiKey(keyRes.value.data.api_key);
      // Fetch funds separately (non-blocking)
      fetchAccountFunds().then(res => {
        if (res.data?.status === 'ok') setAccountFunds(res.data.funds || {});
      }).catch(() => {});
    } catch (err) {
      if (!silent) setError('Failed to load settings');
    }
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // Poll for proxy provisioning updates and auto-reconnect when proxy is ready
  // Poll for proxy provisioning updates and auto-reconnect when proxy is ready
  const provisioningIdsRef = React.useRef(new Set());
  const provisioningKey = accounts.filter(a => a.proxy_status === 'provisioning').map(a => a.id).join(',');
  useEffect(() => {
    const provisioningAccounts = accounts.filter(a => a.proxy_status === 'provisioning');
    provisioningIdsRef.current = new Set(provisioningAccounts.map(a => a.id));

    if (provisioningAccounts.length === 0) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetchAccounts();
        const updatedAccounts = res.data?.accounts || [];
        setAccounts(updatedAccounts);

        for (const acc of updatedAccounts) {
          if (provisioningIdsRef.current.has(acc.id) && acc.proxy_status === 'active' && acc.proxy_ip) {
            provisioningIdsRef.current.delete(acc.id);
            try {
              await reconnectAccount(acc.id);
            } catch (e) { /* silent */ }
          }
        }
      } catch (e) { /* silent polling failure */ }
    }, 10000);
    return () => clearInterval(interval);
  }, [provisioningKey]); // eslint-disable-line

  const handleSaveAccount = async () => {
    setSavingAccount(true);
    setError(null);
    try {
      if (editingAccount) {
        await updateAccount(editingAccount.id, editingAccount);
        setEditingAccount(null);
      } else {
        const name = connectionResult?.profile?.name || newAccount.client_id;
        const payload = { ...newAccount, name, mpin: newAccount.password };
        if (useDefaultKey && defaultApiKey && newAccount.broker !== 'kite') payload.api_key = defaultApiKey;
        const res = await createAccount(payload);
        const msg = res.data?.message || 'Account saved';
        setNewAccount({ broker: 'angelone', api_key: '', api_secret: '', client_id: '', password: '', mpin: '', totp_secret: '', paper_trading: true });
        setShowAddAccount(false);
        setConnectionResult(null);
        if (res.data?.proxy_provisioning) {
          alert(`${msg}\n\nA dedicated proxy IP is being provisioned (~2 min). The IP will appear in the Proxy IP column once ready.`);
        }
      }
      await loadSettings();
    } catch (err) {
      setError('Failed to save account: ' + (err.response?.data?.message || err.message));
    }
    setSavingAccount(false);
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionResult(null);
    try {
      const payload = { ...newAccount, mpin: newAccount.password };
      if (useDefaultKey && defaultApiKey && newAccount.broker !== 'kite') payload.api_key = defaultApiKey;
      const res = await testAccountConnection(payload);
      setConnectionResult(res.data);
    } catch (err) {
      setConnectionResult({ status: 'error', message: 'Connection failed. Check your credentials.' });
    }
    setTestingConnection(false);
  };

  const handleAngelOneLogin = async () => {
    try {
      const res = await getAngelOneLoginUrl();
      if (res.data?.status === 'ok' && res.data?.login_url) window.location.href = res.data.login_url;
      else setError(res.data?.message || 'Could not get login URL');
    } catch (err) { setError('Failed to initiate AngelOne login'); }
  };

  const handleKiteLogin = async (accountId) => {
    try {
      const res = await getKiteLoginUrl(accountId);
      if (res.data?.status === 'ok' && res.data?.login_url) window.location.href = res.data.login_url;
      else setError(res.data?.message || 'Could not get Kite login URL');
    } catch (err) { setError('Failed to initiate Kite login'); }
  };

  const handleSetDataFeed = async (id) => {
    try { await setDataFeedAccount(id); await loadSettings(); } catch { setError('Failed to set data feed account'); }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Delete this broker account?')) return;
    try { await deleteAccount(id); await loadSettings(); } catch { setError('Failed to delete account'); }
  };

  const handleUpdateInstance = async (id, updates) => {
    try { await updateStrategyConfig(id, updates); await loadSettings(); } catch { setError('Failed to update strategy config'); }
  };

  const handleDeleteInstance = async (id) => {
    if (!window.confirm('Delete this strategy instance?')) return;
    try { await deleteStrategyConfig(id); await loadSettings(); } catch { setError('Failed to delete strategy instance'); }
  };

  const handleAddInstance = async (overrides = {}) => {
    try {
      const inst = { ...newInstance, ...overrides };
      const stratName = inst.entry_type === 'manual_single_leg'
        ? `manual_${inst.index}`
        : inst.entry_type === 'rolling_strangle'
        ? `rolling_strangle_${inst.index}`
        : inst.entry_type === 'otm_strangle'
        ? `otm_strangle_${inst.index}_${inst.trading_day}`
        : inst.entry_type === 'ladder_straddle'
        ? `ladder_straddle_${inst.index}`
        : inst.entry_type === 'indicator_aligned'
        ? `indicator_aligned_${inst.index}_${inst.trading_day}`
        : inst.entry_type === 'major_stocks_aligned'
        ? `major_stocks_${inst.index}_${inst.trading_day}`
        : `${inst.entry_type}_${inst.index}_${inst.trading_day}`;
      await createStrategyConfig({
        strategy_name: stratName,
        ...inst,
        account_id: inst.account_id || null,
      });
      setAddingStrategy(false);
      setNewInstance({ entry_type: 'atm_straddle', index: 'Nifty', trading_day: 'Tuesday', adjustment_points: 50, strike_interval: 50, start_time: '09:45', exit_time: '14:40', strike_mode: 'ATM', itm_points: 0, lots: 1, account_id: '', is_active: true, sl_type: 'none', sl_lower: 0, sl_upper: 0, sl_premium: 0, sl_amount: 0, offset_points: 500, rolling_points: 300, hedge_enabled: false, hedge_premium: 3, hedge_lots: 0, hedge_strike_mode: 'premium', hedge_offset_strikes: 5, place_immediately: false, bullish_reference: 'prev_day_close', bearish_reference: 'prev_day_close', stock_weight_threshold: 3, otm_strikes: 1, target_amount: 0, target_exit_pct: 0, sl_partial_amount: 0, sl_partial_exit_pct: 0, timed_lot_add_enabled: false, timed_lot_add_interval_min: 30, timed_lot_add_lots: 1, timed_lot_add_max_lots: 1, timed_lot_add_pnl_gate_enabled: false, timed_lot_add_pnl_threshold_pct: 0 });
      await loadSettings();
    } catch { setError('Failed to add strategy instance'); }
  };

  return (
    <div className="page-settings">
      <div className="dash-page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Manage broker accounts and strategy configurations</p>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {loading && <div className="loading"><div className="spinner" /><p>Loading settings...</p></div>}

      {/* Broker Accounts Section */}
      <section className="settings-section">
        <div className="settings-section-header">
          <h2>Broker Accounts</h2>
          <button className="fetch-btn" onClick={() => setShowAddAccount(!showAddAccount)}>
            {showAddAccount ? 'Cancel' : '+ Add Account'}
          </button>
        </div>

        {showAddAccount && (
          <div className="account-form">
            <h3>Connect Broker Account</h3>
            {!connectionResult || connectionResult.status !== 'ok' ? (
              <>
                <div className="oauth-login-section">
                  <p className="settings-hint">Login securely via your broker — no credentials stored in this app.</p>
                  <div className="oauth-buttons">
                    <button className="fetch-btn btn-oauth btn-angelone" onClick={handleAngelOneLogin}>Login with AngelOne</button>
                    <button className="fetch-btn btn-oauth btn-kite" onClick={handleKiteLogin}>Login with Kite (Zerodha)</button>
                  </div>
                </div>
                <div className="login-divider"><span>or connect manually</span></div>
                <div className="form-grid" style={{ marginBottom: 12 }}>
                  <div className="form-field">
                    <label>Broker</label>
                    <select value={newAccount.broker} onChange={e => setNewAccount({...newAccount, broker: e.target.value})}>
                      <option value="angelone">AngelOne</option>
                      <option value="kite">Kite (Zerodha)</option>
                    </select>
                  </div>
                </div>
                <p className="settings-hint">{newAccount.broker === 'kite' ? 'Enter your Kite Connect API credentials.' : 'Enter your AngelOne account credentials.'}</p>
                {newAccount.broker === 'angelone' && (
                  <div className="api-key-section">
                    <label className="api-key-toggle">
                      <input type="checkbox" checked={useDefaultKey} onChange={e => setUseDefaultKey(e.target.checked)} />
                      {' '}Use shared API key {defaultApiKey ? <span className="api-key-preview">({defaultApiKey.slice(0, 4)}...)</span> : ''}
                    </label>
                    {!useDefaultKey && (
                      <div className="form-field" style={{ marginTop: 8 }}>
                        <label>Custom API Key</label>
                        <input type="text" value={newAccount.api_key} onChange={e => setNewAccount({...newAccount, api_key: e.target.value})} placeholder="Your own SmartAPI key" />
                        <small style={{ color: '#999', fontSize: 11 }}>Get from <a href="https://smartapi.angelone.in/" target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7' }}>smartapi.angelone.in</a> → My Apps → Create/select app → copy API Key</small>
                      </div>
                    )}
                  </div>
                )}
                {newAccount.broker === 'kite' && (
                  <div className="form-grid">
                    <div className="form-field">
                      <label>API Key</label>
                      <input type="text" value={newAccount.api_key} onChange={e => setNewAccount({...newAccount, api_key: e.target.value})} placeholder="Kite Connect API key" />
                    </div>
                    <div className="form-field">
                      <label>API Secret</label>
                      <input type="password" value={newAccount.api_secret} onChange={e => setNewAccount({...newAccount, api_secret: e.target.value})} placeholder="Kite Connect API secret" />
                    </div>
                  </div>
                )}
                <div className="form-grid">
                  <div className="form-field">
                    <label>Client ID</label>
                    <input type="text" value={newAccount.client_id} onChange={e => setNewAccount({...newAccount, client_id: e.target.value})} placeholder={newAccount.broker === 'kite' ? 'e.g. AB1234' : 'e.g. D12345'} />
                  </div>
                  {newAccount.broker === 'angelone' && (
                    <>
                      <div className="form-field">
                        <label>MPIN (4-digit PIN)</label>
                        <input type="password" value={newAccount.password} onChange={e => setNewAccount({...newAccount, password: e.target.value})} placeholder="Your 4-digit MPIN" />
                      </div>
                      <div className="form-field">
                        <label>TOTP Secret</label>
                        <input type="password" value={newAccount.totp_secret} onChange={e => setNewAccount({...newAccount, totp_secret: e.target.value})} placeholder="Base32 secret" />
                      </div>
                    </>
                  )}
                </div>
                <div className="account-form-actions">
                  {newAccount.broker === 'kite' ? (
                    <button className="fetch-btn btn-connect" onClick={handleTestConnection} disabled={testingConnection || !newAccount.api_key || !newAccount.api_secret || !newAccount.client_id}>
                      {testingConnection ? 'Connecting...' : 'Save Kite Account'}
                    </button>
                  ) : (
                    <button className="fetch-btn btn-connect" onClick={handleTestConnection} disabled={testingConnection || (!useDefaultKey && !newAccount.api_key) || !newAccount.client_id || !newAccount.password || !newAccount.totp_secret}>
                      {testingConnection ? 'Connecting...' : 'Connect to AngelOne'}
                    </button>
                  )}
                </div>
                {connectionResult && connectionResult.status === 'error' && (
                  <div className="connection-result connection-error">{connectionResult.message}</div>
                )}
              </>
            ) : connectionResult.profile ? (
              <>
                <div className="profile-card">
                  <div className="profile-card-header">
                    <div>
                      <h4>{connectionResult.profile.name || connectionResult.profile.client_id}</h4>
                      <span className="profile-client-id">{connectionResult.profile.client_id}</span>
                    </div>
                    <span className="connection-badge-ok">Connected</span>
                  </div>
                  <div className="profile-details">
                    {connectionResult.profile.email && (
                      <div className="profile-detail-item"><span className="profile-label">Email</span><span>{connectionResult.profile.email}</span></div>
                    )}
                    <div className="profile-detail-item">
                      <span className="profile-label">Available Funds</span>
                      <span className="profile-funds">₹{connectionResult.funds?.available_cash !== 'N/A' ? Number(connectionResult.funds.available_cash).toLocaleString('en-IN') : 'N/A'}</span>
                    </div>
                  </div>
                  <div className="profile-card-footer">
                    <label><input type="checkbox" checked={newAccount.paper_trading} onChange={e => setNewAccount({...newAccount, paper_trading: e.target.checked})} /> Paper Trading Mode</label>
                  </div>
                </div>
                <div className="account-form-actions">
                  <button className="fetch-btn btn-connect" onClick={handleSaveAccount} disabled={savingAccount}>{savingAccount ? 'Saving...' : 'Save Account'}</button>
                  <button className="fetch-btn" onClick={() => setConnectionResult(null)} disabled={savingAccount}>Back</button>
                </div>
              </>
            ) : (
              <>
                <div className="connection-result connection-success"><p>{connectionResult.message}</p></div>
                <div className="account-form-actions">
                  <button className="fetch-btn" onClick={() => { setConnectionResult(null); setShowAddAccount(false); }}>Done</button>
                </div>
              </>
            )}
          </div>
        )}

        {accounts.length > 0 && (
          <div className="table-wrapper">
            <table className="strat-table responsive-stack-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Broker</th>
                  <th>Client ID</th>
                  <th>Mode</th>
                  <th>Auth</th>
                  <th>Connection</th>
                  <th>Available Funds</th>
                  <th>Proxy IP</th>
                  <th>Data Feed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map(acc => (
                  <React.Fragment key={acc.id}>
                  <tr className={`${expandedAccounts[acc.id] || editingAccount?.id === acc.id ? 'row-expanded-card' : 'row-collapsed'} ${editingAccount?.id === acc.id ? 'row-editing' : ''}`}>
                    <td
                      data-label="Name"
                      className="cell-symbol cell-show-collapsed"
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleAccountExpanded(acc.id)}
                    >
                      <span className="card-chevron" aria-hidden="true">{expandedAccounts[acc.id] || editingAccount?.id === acc.id ? '▾' : '▸'}</span>
                      {acc.name}
                    </td>
                    <td data-label="Broker"><span className={`broker-badge broker-${acc.broker || 'angelone'}`}>{acc.broker === 'kite' ? 'Kite' : 'AngelOne'}</span></td>
                    <td data-label="Client ID">{acc.client_id}</td>
                    <td data-label="Mode">{acc.paper_trading ? 'Paper' : 'Live'}</td>
                    <td data-label="Auth">{acc.login_method === 'oauth' ? 'OAuth' : 'Manual'}</td>
                    <td data-label="Connection" className="cell-show-collapsed">
                      <span className={`connection-dot ${acc.is_connected ? 'connected' : 'disconnected'}`}>
                        {acc.is_connected ? '● Connected' : '○ Disconnected'}
                      </span>
                    </td>
                    <td data-label="Available Funds">
                      {(() => {
                        const f = accountFunds[acc.id];
                        if (!f || !acc.is_connected) return <span className="funds-na">—</span>;
                        const cash = parseFloat(f.available_cash);
                        if (isNaN(cash)) return <span className="funds-na">{f.available_cash}</span>;
                        return (
                          <div className="funds-cell">
                            <span className="funds-amount">₹{cash.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                            {f.used_margin && f.used_margin !== 'N/A' && (
                              <span className="funds-used" title="Used margin">Used: ₹{parseFloat(f.used_margin).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td data-label="Proxy IP">
                      {acc.proxy_status === 'active' ? (
                        <span className="proxy-badge proxy-active" title={`Proxy IP: ${acc.proxy_ip}`}>{acc.proxy_ip}</span>
                      ) : acc.proxy_status === 'provisioning' ? (
                        <span className="proxy-badge proxy-pending" title="Proxy is being provisioned (~2 min)">⏳ Provisioning...</span>
                      ) : acc.proxy_status === 'not_needed' ? (
                        <span className="proxy-badge proxy-na">N/A</span>
                      ) : (
                        <span className="proxy-badge proxy-none">None</span>
                      )}
                    </td>
                    <td data-label="Data Feed">
                      {acc.is_data_feed ? (
                        <span className="data-feed-badge">Active</span>
                      ) : (
                        <button className="btn-sm" onClick={() => handleSetDataFeed(acc.id)}>Set as Data Feed</button>
                      )}
                    </td>
                    <td data-label="Actions" className="cell-actions">
                      <div className="acct-actions">
                        <button className="btn-sm" onClick={() => setEditingAccount(editingAccount?.id === acc.id ? null : {...acc})}>{editingAccount?.id === acc.id ? 'Cancel' : 'Edit'}</button>
                        {acc.broker === 'kite' ? (
                          <button className="btn-sm btn-kite-sm" onClick={() => handleKiteLogin(acc.id)}>Login with Kite</button>
                        ) : (
                          <button className="btn-sm" onClick={async () => {
                            try {
                              const res = await reconnectAccount(acc.id);
                              if (res.data?.status === 'ok') { await loadSettings(); }
                              else { setError(res.data?.message || 'Reconnect failed'); }
                            } catch (err) { setError('Reconnect failed'); }
                          }}>Reconnect</button>
                        )}
                        <button className="btn-sm btn-danger" onClick={() => handleDeleteAccount(acc.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                  {editingAccount?.id === acc.id && (
                    <tr className="row-expanded row-edit-attached"><td colSpan={10} className="cell-full">
                      <div className="strat-edit-card">
                        <div className="strat-edit-section">
                          <span className="strat-edit-label">Account Settings</span>
                          <div className="strat-edit-row">
                            <div className="form-field">
                              <label>Name</label>
                              <input type="text" value={editingAccount.name} onChange={e => setEditingAccount({...editingAccount, name: e.target.value})} />
                            </div>
                            <div className="form-field form-field-check">
                              <label><input type="checkbox" checked={editingAccount.paper_trading} onChange={e => setEditingAccount({...editingAccount, paper_trading: e.target.checked})} /> Paper Trading</label>
                            </div>
                          </div>
                        </div>
                        <div className="strat-edit-actions">
                          <button className="btn-sm btn-primary" onClick={handleSaveAccount}>Save</button>
                          <button className="btn-sm" onClick={() => setEditingAccount(null)}>Cancel</button>
                        </div>
                      </div>
                    </td></tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && accounts.length === 0 && (<p className="empty-msg">No broker accounts configured.</p>)}
      </section>

      {/* Strategy Instances Section */}
      <section className="settings-section">
        <div className="settings-section-header">
          <h2>Strategy Instances</h2>
          <button className="fetch-btn btn-primary" onClick={() => setAddingStrategy(!addingStrategy)}>
            {addingStrategy ? 'Cancel' : '+ Add Strategy'}
          </button>
        </div>
        <p className="settings-hint">Each strategy can run on multiple accounts with different configurations.</p>

        {addingStrategy && (
          <div className="strat-edit-card" style={{ marginBottom: 16 }}>
            <div className="strat-edit-section">
              <span className="strat-edit-label">Strategy</span>
              <div className="strat-edit-row">
                <div className="form-field">
                  <label>Type</label>
                  <select value={newInstance.entry_type} onChange={e => updateNewWithDefaults('entry_type', e.target.value)}>
                    <option value="atm_straddle">ATM Straddle</option>
                    <option value="dynamic_atm_straddle">🧠 Dynamic ATM Straddle</option>
                    <option value="opening_range">Opening Range</option>
                    <option value="rolling_strangle">Rolling Strangle</option>
                    <option value="otm_strangle">OTM Strangle</option>
                    <option value="ladder_straddle">Ladder Straddle</option>
                    <option value="indicator_aligned">Indicator Aligned</option>
                    <option value="major_stocks_aligned">Major Stocks Aligned</option>
                    <option value="hedge_only">Hedge Only (Long Strangle)</option>
                  </select>
                  <small className="field-hint">{newInstance.entry_type === 'hedge_only' ? 'Hedge Only: BUYS one OTM CE + one OTM PE (long strangle). No selling. Pure long-vol/disaster hedge.' : newInstance.entry_type === 'otm_strangle' ? 'OTM Strangle: sells CE at ATM + N×interval and PE at ATM − N×interval. Rolls on Adj Pts move just like ATM Straddle.' : 'ATM Straddle sells at current price. Opening Range uses 15-min candle. Rolling Strangle = auto OTM strangle with rolls. Ladder = 3 straddles with offset rolls. Indicator Aligned = sells PE/CE when all 4 dashboard indicators agree. Major Stocks Aligned = sells when all constituents ≥ weight% move one way. For one-off single/multi-leg orders use the Quick Order page instead.'}</small>
                </div>
                <div className="form-field">
                  <label>Index</label>
                  <select value={newInstance.index} onChange={e => updateNewWithDefaults('index', e.target.value)}>
                    <option value="Nifty">Nifty</option>
                    <option value="BankNifty">BankNifty</option>
                    <option value="Sensex">Sensex</option>
                  </select>
                  <small className="field-hint">Which index to trade options on</small>
                </div>
                {newInstance.entry_type !== 'manual_single_leg' && newInstance.entry_type !== 'rolling_strangle' && newInstance.entry_type !== 'ladder_straddle' && newInstance.entry_type !== 'hedge_only' && (<>
                <div className="form-field">
                  <label>Day</label>
                  <select value={newInstance.trading_day} onChange={e => updateNewWithDefaults('trading_day', e.target.value)}>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                    <option value="Daily">Daily</option>
                  </select>
                  <small className="field-hint">Which day of the week to run this strategy</small>
                </div>
                {newInstance.entry_type !== 'dynamic_atm_straddle' && (
                <div className="form-field">
                  <label>Adj Pts</label>
                  <input type="number" value={newInstance.adjustment_points} onChange={e => setNewInstance({...newInstance, adjustment_points: parseInt(e.target.value) || 50})} min="10" step="10" />
                  <small className="field-hint">Re-enter at new ATM if index moves these many points</small>
                </div>
                )}
                <div className="form-field">
                  <label>Strike Int</label>
                  <select value={newInstance.strike_interval} onChange={e => setNewInstance({...newInstance, strike_interval: parseInt(e.target.value)})}>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <small className="field-hint">Strike price rounding interval (50 or 100)</small>
                </div>
                {newInstance.entry_type === 'opening_range' && (
                  <div className="form-field form-field-check">
                    <label><input type="checkbox" checked disabled /> Breakout</label>
                  </div>
                )}
                </>)}
                {newInstance.entry_type === 'manual_single_leg' && (
                  <div className="form-field" style={{ gridColumn: 'span 2' }}>
                    <small className="field-hint" style={{ color: '#58a6ff' }}>
                      Manual Trade: configure strike, stop loss, and entry/exit from the Manual Trade page after creating.
                    </small>
                  </div>
                )}
                {newInstance.entry_type === 'rolling_strangle' && (
                  <>
                  <div className="form-field">
                    <label>Offset Pts</label>
                    <input type="number" value={newInstance.offset_points} onChange={e => setNewInstance({...newInstance, offset_points: parseInt(e.target.value) || 500})} min="50" step="50" />
                    <small className="field-hint">Initial OTM offset from ATM (±points)</small>
                  </div>
                  <div className="form-field">
                    <label>Rolling Pts</label>
                    <input type="number" value={newInstance.rolling_points} onChange={e => setNewInstance({...newInstance, rolling_points: parseInt(e.target.value) || 300})} min="50" step="50" />
                    <small className="field-hint">Points movement to trigger adjustment</small>
                  </div>
                  <div className="form-field">
                    <label>Strike Int</label>
                    <select value={newInstance.strike_interval} onChange={e => setNewInstance({...newInstance, strike_interval: parseInt(e.target.value)})}>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <small className="field-hint">Strike price rounding interval</small>
                  </div>
                  </>
                )}
                {newInstance.entry_type === 'ladder_straddle' && (
                  <>
                  <div className="form-field">
                    <label>Offset Pts</label>
                    <input type="number" value={newInstance.offset_points} onChange={e => setNewInstance({...newInstance, offset_points: parseInt(e.target.value) || 100})} min="50" step="50" />
                    <small className="field-hint">Distance between ladder straddle strikes</small>
                  </div>
                  <div className="form-field">
                    <label>Strike Int</label>
                    <select value={newInstance.strike_interval} onChange={e => setNewInstance({...newInstance, strike_interval: parseInt(e.target.value)})}>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <small className="field-hint">Strike price rounding interval</small>
                  </div>
                  </>
                )}
                {newInstance.entry_type === 'otm_strangle' && (
                  <div className="form-field">
                    <label>OTM Strikes</label>
                    <input type="number" value={newInstance.otm_strikes} onChange={e => setNewInstance({...newInstance, otm_strikes: parseInt(e.target.value) || 1})} min="1" step="1" />
                    <small className="field-hint">1 = +1 step OTM, 2 = +2 steps, … (CE = ATM + N×interval, PE = ATM − N×interval)</small>
                  </div>
                )}
                {newInstance.entry_type === 'hedge_only' && (
                  <>
                  <div className="form-field">
                    <label>Day</label>
                    <select value={newInstance.trading_day} onChange={e => setNewInstance({...newInstance, trading_day: e.target.value})}>
                      <option value="Daily">Daily</option>
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                    </select>
                    <small className="field-hint">Run hedge on these days only</small>
                  </div>
                  <div className="form-field">
                    <label>Strike Int</label>
                    <select value={newInstance.strike_interval} onChange={e => setNewInstance({...newInstance, strike_interval: parseInt(e.target.value)})}>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <small className="field-hint">Strike price rounding interval</small>
                  </div>
                  </>
                )}
                {newInstance.entry_type === 'indicator_aligned' && (
                  <>
                  <div className="form-field">
                    <label>Day</label>
                    <select value={newInstance.trading_day} onChange={e => updateNewWithDefaults('trading_day', e.target.value)}>
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                      <option value="Daily">Daily</option>
                    </select>
                    <small className="field-hint">Day to run this strategy</small>
                  </div>
                  <div className="form-field">
                    <label>Bullish Ref</label>
                    <select value={newInstance.bullish_reference} onChange={e => setNewInstance({...newInstance, bullish_reference: e.target.value})}>
                      <option value="prev_day_close">Prev Day Close</option>
                      <option value="prev_day_low">Prev Day Low</option>
                    </select>
                    <small className="field-hint">PE strike reference when all 4 bullish (floors to interval)</small>
                  </div>
                  <div className="form-field">
                    <label>Bearish Ref</label>
                    <select value={newInstance.bearish_reference} onChange={e => setNewInstance({...newInstance, bearish_reference: e.target.value})}>
                      <option value="prev_day_close">Prev Day Close</option>
                      <option value="prev_day_high">Prev Day High</option>
                    </select>
                    <small className="field-hint">CE strike reference when all 4 bearish (ceils to interval)</small>
                  </div>
                  <div className="form-field">
                    <label>Strike Int</label>
                    <select value={newInstance.strike_interval} onChange={e => setNewInstance({...newInstance, strike_interval: parseInt(e.target.value)})}>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <small className="field-hint">Strike price rounding interval</small>
                  </div>
                  </>
                )}
                {newInstance.entry_type === 'major_stocks_aligned' && (
                  <>
                  <div className="form-field">
                    <label>Day</label>
                    <select value={newInstance.trading_day} onChange={e => updateNewWithDefaults('trading_day', e.target.value)}>
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                      <option value="Daily">Daily</option>
                    </select>
                    <small className="field-hint">Day to run this strategy</small>
                  </div>
                  <div className="form-field">
                    <label>Weight ≥ (%)</label>
                    <select value={newInstance.stock_weight_threshold} onChange={e => setNewInstance({...newInstance, stock_weight_threshold: parseFloat(e.target.value)})}>
                      <option value={2}>2%</option>
                      <option value={3}>3%</option>
                      <option value={4}>4%</option>
                      <option value={5}>5%</option>
                    </select>
                    <small className="field-hint">Only stocks with weight ≥ this are considered "major"</small>
                  </div>
                  <div className="form-field">
                    <label>Bullish Ref</label>
                    <select value={newInstance.bullish_reference} onChange={e => setNewInstance({...newInstance, bullish_reference: e.target.value})}>
                      <option value="prev_day_close">Prev Day Close</option>
                      <option value="prev_day_low">Prev Day Low</option>
                    </select>
                    <small className="field-hint">PE strike reference when all stocks bullish</small>
                  </div>
                  <div className="form-field">
                    <label>Bearish Ref</label>
                    <select value={newInstance.bearish_reference} onChange={e => setNewInstance({...newInstance, bearish_reference: e.target.value})}>
                      <option value="prev_day_close">Prev Day Close</option>
                      <option value="prev_day_high">Prev Day High</option>
                    </select>
                    <small className="field-hint">CE strike reference when all stocks bearish</small>
                  </div>
                  <div className="form-field">
                    <label>Strike Int</label>
                    <select value={newInstance.strike_interval} onChange={e => setNewInstance({...newInstance, strike_interval: parseInt(e.target.value)})}>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <small className="field-hint">Strike price rounding interval</small>
                  </div>
                  </>
                )}
              </div>
            </div>
            <div className="strat-edit-section">
              <span className="strat-edit-label">Stop Loss</span>
              <div className="strat-edit-row">
                <div className="form-field">
                  <label>SL Type</label>
                  <select value={newInstance.sl_type} onChange={e => setNewInstance({...newInstance, sl_type: e.target.value})}>
                    <option value="none">None</option>
                    {newInstance.entry_type !== 'hedge_only' && <option value="spot">Spot Level</option>}
                    <option value="premium">{newInstance.entry_type === 'hedge_only' ? 'Combined Premium ≤' : 'Combined Premium ≥'}</option>
                    <option value="amount">Loss Amount (₹)</option>
                  </select>
                  <small className="field-hint">Type of stop loss trigger</small>
                </div>
                {newInstance.sl_type === 'spot' && (<>
                  <div className="form-field">
                    <label>SL Lower</label>
                    <input type="number" value={newInstance.sl_lower} onChange={e => setNewInstance({...newInstance, sl_lower: parseFloat(e.target.value) || 0})} min="0" step="50" placeholder="0 = off" />
                    <small className="field-hint">Exit if spot drops below this level</small>
                  </div>
                  <div className="form-field">
                    <label>SL Upper</label>
                    <input type="number" value={newInstance.sl_upper} onChange={e => setNewInstance({...newInstance, sl_upper: parseFloat(e.target.value) || 0})} min="0" step="50" placeholder="0 = off" />
                    <small className="field-hint">Exit if spot rises above this level</small>
                  </div>
                </>)}
                {newInstance.sl_type === 'premium' && (
                  <div className="form-field">
                    <label>Premium Threshold</label>
                    <input type="number" value={newInstance.sl_premium} onChange={e => setNewInstance({...newInstance, sl_premium: parseFloat(e.target.value) || 0})} min="0" step="10" placeholder="e.g. 350" />
                    <small className="field-hint">{newInstance.entry_type === 'hedge_only' ? 'Exit if combined CE+PE premium drops below this (long decay)' : 'Exit if combined CE+PE premium exceeds this'}</small>
                  </div>
                )}
                {newInstance.sl_type === 'amount' && (
                  <div className="form-field">
                    <label>Max Loss (₹)</label>
                    <input type="number" value={newInstance.sl_amount} onChange={e => setNewInstance({...newInstance, sl_amount: parseFloat(e.target.value) || 0})} min="0" step="500" placeholder="e.g. 5000" />
                    <small className="field-hint">Exit if strategy loss exceeds this amount</small>
                  </div>
                )}
              </div>
            </div>
            {(newInstance.entry_type === 'atm_straddle' || newInstance.entry_type === 'otm_strangle') && (
            <div className="strat-edit-section">
              <span className="strat-edit-label">Partial Exits (one-shot per day)</span>
              <div className="strat-edit-row">
                <div className="form-field">
                  <label>Target Amount (₹)</label>
                  <input type="number" value={newInstance.target_amount} onChange={e => setNewInstance({...newInstance, target_amount: parseFloat(e.target.value) || 0})} min="0" step="500" placeholder="0 = off" />
                  <small className="field-hint">When profit ≥ this, exit a slice of lots</small>
                </div>
                <div className="form-field">
                  <label>Target Exit %</label>
                  <input type="number" value={newInstance.target_exit_pct} onChange={e => setNewInstance({...newInstance, target_exit_pct: parseFloat(e.target.value) || 0})} min="0" max="99" step="5" placeholder="e.g. 30" />
                  <small className="field-hint">% of original lots to exit on target hit</small>
                </div>
                <div className="form-field">
                  <label>Partial SL Amount (₹)</label>
                  <input type="number" value={newInstance.sl_partial_amount} onChange={e => setNewInstance({...newInstance, sl_partial_amount: parseFloat(e.target.value) || 0})} min="0" step="500" placeholder="0 = off" />
                  <small className="field-hint">When loss ≥ this, exit a slice of lots</small>
                </div>
                <div className="form-field">
                  <label>Partial SL Exit %</label>
                  <input type="number" value={newInstance.sl_partial_exit_pct} onChange={e => setNewInstance({...newInstance, sl_partial_exit_pct: parseFloat(e.target.value) || 0})} min="0" max="99" step="5" placeholder="e.g. 30" />
                  <small className="field-hint">% of original lots to exit on partial SL hit</small>
                </div>
              </div>
            </div>
            )}
            {newInstance.entry_type === 'atm_straddle' && (
            <div className="strat-edit-section">
              <span className="strat-edit-label">Timed Lot Additions</span>
              <div className="strat-edit-row">
                <div className="form-field">
                  <label>Enable Timed Add</label>
                  <select value={newInstance.timed_lot_add_enabled ? 'on' : 'off'} onChange={e => setNewInstance({...newInstance, timed_lot_add_enabled: e.target.value === 'on'})}>
                    <option value="off">Off</option>
                    <option value="on">On</option>
                  </select>
                  <small className="field-hint">Turn periodic lot additions ON/OFF</small>
                </div>
                <div className="form-field">
                  <label>Interval (min)</label>
                  <input type="number" value={newInstance.timed_lot_add_interval_min} onChange={e => setNewInstance({...newInstance, timed_lot_add_interval_min: Math.max(1, parseInt(e.target.value) || 30)})} min="1" step="1" disabled={!newInstance.timed_lot_add_enabled} />
                  <small className="field-hint">Run timed add check every N minutes</small>
                </div>
                <div className="form-field">
                  <label>Lots Per Add</label>
                  <input type="number" value={newInstance.timed_lot_add_lots} onChange={e => setNewInstance({...newInstance, timed_lot_add_lots: Math.max(1, parseInt(e.target.value) || 1)})} min="1" step="1" disabled={!newInstance.timed_lot_add_enabled} />
                  <small className="field-hint">Lots to add when timed check passes</small>
                </div>
                <div className="form-field">
                  <label>Max Total Lots</label>
                  <input type="number" value={newInstance.timed_lot_add_max_lots} onChange={e => setNewInstance({...newInstance, timed_lot_add_max_lots: Math.max(newInstance.lots || 1, parseInt(e.target.value) || (newInstance.lots || 1))})} min={newInstance.lots || 1} step="1" disabled={!newInstance.timed_lot_add_enabled} />
                  <small className="field-hint">Stop adding after this total lot cap</small>
                </div>
                <div className="form-field">
                  <label>P&L% Gate</label>
                  <select value={newInstance.timed_lot_add_pnl_gate_enabled ? 'on' : 'off'} onChange={e => setNewInstance({...newInstance, timed_lot_add_pnl_gate_enabled: e.target.value === 'on'})} disabled={!newInstance.timed_lot_add_enabled}>
                    <option value="off">Off</option>
                    <option value="on">On</option>
                  </select>
                  <small className="field-hint">Skip timed add if threshold reached</small>
                </div>
                <div className="form-field">
                  <label>P&L Threshold %</label>
                  <input type="number" value={newInstance.timed_lot_add_pnl_threshold_pct} onChange={e => setNewInstance({...newInstance, timed_lot_add_pnl_threshold_pct: parseFloat(e.target.value) || 0})} min="0" step="0.1" disabled={!newInstance.timed_lot_add_enabled || !newInstance.timed_lot_add_pnl_gate_enabled} placeholder="e.g. 15" />
                  <small className="field-hint">Open P&L% of combined sold premium</small>
                </div>
              </div>
            </div>
            )}
            {newInstance.entry_type === 'hedge_only' && (
            <div className="strat-edit-section">
              <span className="strat-edit-label">Hedge Selection</span>
              <div className="strat-edit-row">
                <div className="form-field">
                  <label>Strike Mode</label>
                  <select value={newInstance.hedge_strike_mode} onChange={e => setNewInstance({...newInstance, hedge_strike_mode: e.target.value})}>
                    <option value="premium">Premium-based</option>
                    <option value="offset">Offset-based</option>
                  </select>
                  <small className="field-hint">Premium: scan OTM for ≈ target ₹. Offset: ATM ± N strikes</small>
                </div>
                {newInstance.hedge_strike_mode === 'premium' && (
                  <div className="form-field">
                    <label>Target Premium <span className="unit">(₹)</span></label>
                    <input type="number" value={newInstance.hedge_premium} onChange={e => setNewInstance({...newInstance, hedge_premium: parseFloat(e.target.value) || 3})} min="0.5" step="0.5" />
                    <small className="field-hint">Nifty ≈ ₹3 · Sensex ≈ ₹10</small>
                  </div>
                )}
                {newInstance.hedge_strike_mode === 'offset' && (
                  <div className="form-field">
                    <label>Strikes OTM</label>
                    <input type="number" value={newInstance.hedge_offset_strikes} onChange={e => setNewInstance({...newInstance, hedge_offset_strikes: parseInt(e.target.value) || 5})} min="1" step="1" />
                    <small className="field-hint">CE = ATM + N×interval, PE = ATM − N×interval</small>
                  </div>
                )}
              </div>
            </div>
            )}
            {newInstance.entry_type !== 'manual_single_leg' && newInstance.entry_type !== 'hedge_only' && (
            <div className="strat-edit-section">
              <span className="strat-edit-label">Hedge</span>
              <div style={{flex: 1}}>
                <div className="hedge-toggle-row">
                  <label className="hedge-switch">
                    <input type="checkbox" checked={newInstance.hedge_enabled} onChange={e => setNewInstance({...newInstance, hedge_enabled: e.target.checked})} />
                    <span className="slider"></span>
                  </label>
                  <span className="hedge-toggle-label" onClick={() => setNewInstance({...newInstance, hedge_enabled: !newInstance.hedge_enabled})}>
                    OTM Protection
                    <span className={`hedge-badge ${newInstance.hedge_enabled ? 'badge-on' : 'badge-off'}`}>{newInstance.hedge_enabled ? 'ON' : 'OFF'}</span>
                  </span>
                </div>
                {newInstance.hedge_enabled && (
                  <div className="hedge-fields">
                    <div className="form-field">
                      <label>Target Premium <span className="unit">(₹)</span></label>
                      <input type="number" value={newInstance.hedge_premium} onChange={e => setNewInstance({...newInstance, hedge_premium: parseFloat(e.target.value) || 3})} min="0.5" step="0.5" />
                      <small className="field-hint">Nifty ≈ ₹3 · Sensex ≈ ₹10</small>
                    </div>
                    <div className="form-field">
                      <label>Hedge Lots</label>
                      <input type="number" value={newInstance.hedge_lots} onChange={e => setNewInstance({...newInstance, hedge_lots: parseInt(e.target.value) || 0})} min="0" step="1" />
                      <small className="field-hint">0 = match strategy lots</small>
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}
            <div className="strat-edit-section">
              <span className="strat-edit-label">Execution</span>
              <div className="strat-edit-row">
                <div className="form-field">
                  <label>Account</label>
                  <select value={newInstance.account_id} onChange={e => setNewInstance({...newInstance, account_id: e.target.value ? parseInt(e.target.value) : ''})}>
                    <option value="">Paper Trading</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                  <small className="field-hint">Broker account to execute on. Paper = simulated</small>
                </div>
                <div className="form-field form-field-wide">
                  <label>Entry</label>
                  <input type="time" value={newInstance.start_time} onChange={e => setNewInstance({...newInstance, start_time: e.target.value})} />
                  <small className="field-hint">{newInstance.entry_type === 'manual_single_leg' ? 'Strategy activates at this time' : 'Time to enter the position'}</small>
                </div>
                <div className="form-field form-field-wide">
                  <label>Exit</label>
                  <input type="time" value={newInstance.exit_time} onChange={e => setNewInstance({...newInstance, exit_time: e.target.value})} />
                  <small className="field-hint">Time to square off all positions</small>
                </div>
                {newInstance.entry_type !== 'manual_single_leg' && newInstance.entry_type !== 'rolling_strangle' && newInstance.entry_type !== 'ladder_straddle' && newInstance.entry_type !== 'hedge_only' && (<>
                <div className="form-field">
                  <label>Mode</label>
                  <select value={newInstance.strike_mode} onChange={e => setNewInstance({...newInstance, strike_mode: e.target.value})}>
                    <option value="ATM">ATM</option>
                    <option value="ITM">ITM</option>
                  </select>
                  <small className="field-hint">ATM = at the money. ITM = sell deeper in the money</small>
                </div>
                {newInstance.strike_mode === 'ITM' && (
                  <div className="form-field">
                    <label>ITM Pts</label>
                    <input type="number" value={newInstance.itm_points} onChange={e => setNewInstance({...newInstance, itm_points: parseInt(e.target.value) || 0})} min="0" step="50" />
                    <small className="field-hint">How many points ITM to sell (CE strike below, PE strike above)</small>
                  </div>
                )}
                </>)}
                <div className="form-field">
                  <label>Lots</label>
                  <input type="number" value={newInstance.lots} onChange={e => setNewInstance({...newInstance, lots: parseInt(e.target.value) || 1})} min="1" />
                  <small className="field-hint">Number of lots per leg</small>
                </div>
              </div>
            </div>
            <div className="strat-edit-actions">
              <button className="btn-sm btn-primary" onClick={() => handleAddInstance()}>Create</button>
              <button className="btn-sm btn-warn" onClick={() => handleAddInstance({ place_immediately: true })}>⚡ Create &amp; Place Now</button>
              <button className="btn-sm" onClick={() => setAddingStrategy(false)}>Cancel</button>
            </div>
          </div>
        )}

        {strategyConfigs.length === 0 && !addingStrategy && (<p className="empty-msg">No strategy instances configured.</p>)}
        {strategyConfigs.length > 0 && (<>
          <div className="inst-filters">
            <select value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">All Types</option>
              <option value="atm_straddle">ATM Straddle</option>
              <option value="dynamic_atm_straddle">Dynamic ATM Straddle</option>
              <option value="opening_range">Opening Range</option>
              <option value="rolling_strangle">Rolling Strangle</option>
              <option value="ladder_straddle">Ladder Straddle</option>
              <option value="hedge_only">Hedge Only</option>
            </select>
            <select value={filterIndex} onChange={e => setFilterIndex(e.target.value)}>
              <option value="">All Indices</option>
              <option value="Nifty">Nifty</option>
              <option value="BankNifty">BankNifty</option>
              <option value="Sensex">Sensex</option>
            </select>
            <select value={filterDay} onChange={e => setFilterDay(e.target.value)}>
              <option value="">All Days</option>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
            </select>
          </div>
          <div className="table-wrapper">
            <table className="inst-table responsive-stack-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Index</th>
                  <th>Day</th>
                  <th>Adj Pts</th>
                  <th>Strike</th>
                  <th>Stop Loss</th>
                  <th>Hedge</th>
                  <th>Account</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>Mode</th>
                  <th>Lots</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {strategyConfigs
                  .filter(inst => (!filterType || inst.entry_type === filterType) && (!filterIndex || inst.index === filterIndex) && (!filterDay || inst.trading_day === filterDay))
                  .map(inst => (
                  <StrategyInstance key={inst.id} instance={inst} accounts={accounts} onSave={handleUpdateInstance} onDelete={handleDeleteInstance} />
                ))}
              </tbody>
            </table>
          </div>
        </>)}
      </section>
    </div>
  );
}
